import HttpError from './httpError'

export type FetchConverter<T> = (fetchResponse: Response) => T
export type CacheKeyGenerator = (url: string) => string

/**
 * Implement default cache key from the URL
 * Default implementation returns remaining part after /api/v1 or /api/v2 or url itself
 * @param url
 */
export const defaultCacheKeyGenerator: CacheKeyGenerator = (
  url: string
): string => url.split('/api/v')[1] || url

export const defaultRequestInit: RequestInit = {
  credentials: 'same-origin',
  method: 'GET',
  headers: {
    Accept: 'application/json',
  },
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const defaultFetchConverter: FetchConverter<any> = (
  response: Response
): Promise<any> => response.json() // eslint-disable-line @typescript-eslint/no-explicit-any

/**
 * createCachedFetch return a function which cache fetch promises for the Angular service calls
 * @param fetchConverter a function which convert fetch() response to the cached data
 * @param fetchRequestInit a RequestInit block for the fetch() call
 * @param cacheKeyFn a function to generate cache key out of the supplied url
 */
export default function createCachedFetch<T>(
  fetchConverter: FetchConverter<T> = defaultFetchConverter,
  fetchRequestInit: RequestInit = defaultRequestInit,
  cacheKeyFn: CacheKeyGenerator = defaultCacheKeyGenerator
): (url: string) => Promise<T> {
  const promisesCache = {}
  return function cachedFetch(url: string): Promise<T> {
    const cacheKey = cacheKeyFn(url)

    // Return a promise if the request is in flight instead of make a bunch of new requests
    if (promisesCache[cacheKey]) {
      return promisesCache[cacheKey]
    }

    const fetchPromise = fetch(url, fetchRequestInit)
      .then((response) => {
        if (!response.ok) {
          throw new HttpError(response.status, response.statusText)
        }
        return response
      })
      .then(fetchConverter)
      .finally(() => {
        delete promisesCache[cacheKey]
      })
    promisesCache[cacheKey] = fetchPromise
    return fetchPromise
  }
}
